import type { ProductLookupRow } from '~/features/deliveries/types/delivery-runs.types';

/** Per-attribute delivery outcome, distinct from the row-level `outcome` —
 *  a row can be `DELIVERED` overall while one field was truncated or dropped. */
export type DeliveredFieldStatus = 'sent' | 'truncated' | 'failed' | 'not_sent' | 'unknown';

/**
 * Candidate destination attribute keys for one enrichment field name.
 *
 * UNVERIFIED — the contract's own sample response uses lower_snake GMC-native
 * keys (`title`, `retailer_id`, `brand`, `color`) in `deliveredKeys`, so this
 * starts as an identity mapping plus the enrichment→curation plural alias
 * (mirroring `ENRICHMENT_TO_CURATION_FIELD` in `catalog-operations.mapper.ts`)
 * and should be corrected once a real delivered batch is available — see
 * `docs/delivery-compare-view.md` "Open items". Any `deliveredKeys` entry not
 * claimed by one of these lists surfaces via {@link unmappedDeliveredKeys} so
 * nothing silently hides.
 */
const ENRICHMENT_TO_DELIVERED_KEYS: Record<string, string[]> = {
  title: ['title'],
  structured_title: ['structured_title', 'title'],
  description: ['description'],
  structured_description: ['structured_description', 'description'],
  product_highlight: ['product_highlights', 'product_highlight'],
  product_detail: ['product_details', 'product_detail'],
  product_type: ['product_types', 'product_type', 'google_product_category'],
  color: ['color'],
  material: ['material'],
  pattern: ['pattern'],
  product_qa: ['product_qa'],
};

/**
 * Candidate destination/attribute-picker keys for one enrichment field name —
 * exported so the pre-delivery preview's proposed-status computation
 * (`compute-proposed-delivery-status.ts`) can join the compare table's
 * `ProductOptimization.field` vocabulary against the attribute picker's
 * `DeliverySelection.attributeKeys` vocabulary through the same alias table
 * used for the real post-delivery join, rather than assuming the two spaces
 * are always spelled identically (see `product_type`/`google_product_category`).
 */
export function candidateKeysFor(field: string): string[] {
  return ENRICHMENT_TO_DELIVERED_KEYS[field] ?? [field];
}

/**
 * Classifies one enriched attribute's delivery outcome against a matched audit
 * row's field-audit arrays. `unknown` covers both "no audit row" and "the run
 * recorded no field audit at all" (`deliveredKeys == null`) — both mean the
 * question can't be answered, which must not render the same as "not sent".
 */
export function deliveredFieldStatus(
  field: string,
  row: ProductLookupRow | undefined,
): DeliveredFieldStatus {
  if (!row) return 'unknown';
  const candidates = candidateKeysFor(field);
  const deliveredKeys = row.deliveredKeys;
  if (deliveredKeys == null) return 'unknown';
  const truncatedFields = row.truncatedFields ?? [];
  const fieldsFailed = row.fieldsFailed ?? [];
  if (candidates.some((key) => truncatedFields.includes(key))) return 'truncated';
  if (candidates.some((key) => fieldsFailed.includes(key))) return 'failed';
  if (candidates.some((key) => deliveredKeys.includes(key))) return 'sent';
  return 'not_sent';
}

/** How many attribute fields fall into each delivery status, for the
 *  clickable status legend above the attribute table. Statuses with a zero
 *  count are simply absent from the result — the legend renders only the
 *  statuses that actually occur for this product. */
export function tallyDeliveredFieldStatuses(
  fields: readonly string[],
  row: ProductLookupRow | undefined,
): Partial<Record<DeliveredFieldStatus, number>> {
  const counts: Partial<Record<DeliveredFieldStatus, number>> = {};
  for (const field of fields) {
    const status = deliveredFieldStatus(field, row);
    counts[status] = (counts[status] ?? 0) + 1;
  }
  return counts;
}

/** The per-field error text for a `failed` status, when the row carries one. */
export function deliveredFieldError(
  field: string,
  row: ProductLookupRow | undefined,
): string | undefined {
  if (!row?.fieldErrors) return undefined;
  for (const key of candidateKeysFor(field)) {
    const message = row.fieldErrors[key];
    if (message) return message;
  }
  return undefined;
}

/** `deliveredKeys` entries no known attribute claims — surfaced in an "Other
 *  delivered fields" footer so a real delivered key never hides silently just
 *  because the mapping above hasn't been corrected against a live batch yet. */
export function unmappedDeliveredKeys(
  fields: readonly string[],
  row: ProductLookupRow | undefined,
): string[] {
  if (!row?.deliveredKeys) return [];
  const known = new Set(fields.flatMap((field) => candidateKeysFor(field)));
  return row.deliveredKeys.filter((key) => !known.has(key));
}

/**
 * Best-effort read of one field's value out of the delivered payload
 * (`transformedData`, only present when the row was fetched with
 * `includePayload: true` and the run captured payloads). The payload's own
 * shape is destination-specific (a GMC product resource vs. a Meta catalog
 * item) and unverified here, so this tries the same candidate keys used for
 * `deliveredKeys` against the parsed JSON's top level and returns `undefined`
 * — not a guess — when nothing matches, so the caller can show "sent" without
 * a fabricated value rather than silently displaying the wrong field.
 *
 * Handles two possible wire shapes for the same reason: the existing product
 * timeline endpoint's `transformedData` is already a parsed object (`JsonMap`,
 * per `meta-delivery.types.ts`), but the batch lookup contract's own sample
 * response shows it as a JSON-encoded *string* (`""` when not captured). Since
 * BE PR #627 is unmerged, this accepts either rather than guessing which one
 * a live response actually sends — verify against dev and simplify once known.
 */
export function extractDeliveredValue(
  field: string,
  row: ProductLookupRow | undefined,
): string | undefined {
  const raw = row?.transformedData;
  let payload: Record<string, unknown>;
  if (typeof raw === 'string') {
    if (raw.trim() === '') return undefined;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return undefined;
    }
    if (typeof parsed !== 'object' || parsed === null) return undefined;
    payload = parsed as Record<string, unknown>;
  } else if (raw != null && typeof raw === 'object') {
    payload = raw;
  } else {
    return undefined;
  }
  for (const key of candidateKeysFor(field)) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
    if (Array.isArray(value)) {
      const joined = value.filter((v): v is string => typeof v === 'string').join(', ');
      if (joined) return joined;
    }
  }
  return undefined;
}
